import { supabase } from '../supabase';

/** What a shipment shows right after its tracking is activated. */
export type TrackingActivationState =
  | 'tracking'
  | 'locating'
  | 'scheduled'
  | 'queued'
  | 'not_found'
  | 'no_identifier'
  | 'invalid_identifier'
  | 'no_dates'
  | 'expired'
  | 'error';

export interface TrackingActivationShipment {
  shipment_id: number;
  identifier: string | null;
  mode: 'ocean' | 'air' | null;
  state: TrackingActivationState;
  journey_id: string | null;
  registration_opens_on: string | null;
  live_eta: string | null;
  destination_name: string | null;
  latest_event: {
    event_type: string;
    location_name: string | null;
    occurred_at: string;
  } | null;
  has_route: boolean;
}

/** Server-side activation of uploaded shipments (queued by the upload). */
export interface TrackingActivationRow {
  shipment_id: number;
  status: 'pending' | 'running' | 'done';
  result: TrackingActivationShipment | null;
}

/**
 * Activation progress for uploaded shipments. The upload itself queues them
 * and the server activates them even if this page is closed; this only reads.
 */
export async function getTrackingActivations(
  shipmentIds: number[]
): Promise<TrackingActivationRow[]> {
  const rows: TrackingActivationRow[] = [];
  for (let offset = 0; offset < shipmentIds.length; offset += 100) {
    const { data, error } = await supabase
      .from('shipment_tracking_activations')
      .select('shipment_id, status, result')
      .in('shipment_id', shipmentIds.slice(offset, offset + 100));
    if (error) throw error;
    rows.push(...((data ?? []) as unknown as TrackingActivationRow[]));
  }
  return rows;
}

/** Uploaded shipments, found by reference (the import only returns counts). */
export async function findShipmentsByReference(
  exporterId: number,
  season: string,
  speciesId: number,
  references: string[]
): Promise<Array<{ id: number; reference: string }>> {
  const unique = [...new Set(references.map((r) => r.trim()).filter(Boolean))];
  const found: Array<{ id: number; reference: string }> = [];
  for (let offset = 0; offset < unique.length; offset += 100) {
    const { data, error } = await supabase
      .from('shipments')
      .select('id, reference_number')
      .eq('exporter_id', exporterId)
      .eq('season', season)
      .eq('species_id', speciesId)
      .in('reference_number', unique.slice(offset, offset + 100));
    if (error) throw error;
    for (const row of data ?? [])
      found.push({
        id: Number(row.id),
        reference: String(row.reference_number),
      });
  }
  return found;
}

/**
 * Read-only preview with shipments already tracked: what the activation
 * panel would show for them. No provider calls, no credits, no writes.
 */
interface PreviewJourneyRow {
  id: string;
  mode: 'ocean' | 'air';
  identifier_value: string;
  status: string;
  provider_status: string | null;
  live_eta: string | null;
  destination_name: string | null;
  registration_opens_on: string | null;
  route_synced_at?: string | null;
  shipment_tracking_links: Array<{ shipment_id: number }> | null;
}

export async function getTrackingActivationPreview(
  limit = 8
): Promise<TrackingActivationShipment[]> {
  const query = (columns: string) =>
    supabase
      .from('shipment_tracking_journeys')
      .select(
        `id, mode, identifier_value, status, provider_status, live_eta, destination_name, registration_opens_on, shipment_tracking_links(shipment_id)${columns}`
      )
      .in('status', ['active', 'scheduled', 'rejected', 'failed'])
      .order('updated_at', { ascending: false })
      .limit(limit);
  let { data: journeys, error } = await query(', route_synced_at');
  // Databases before migration 20261109120000 have no route columns.
  if (error?.code === '42703') ({ data: journeys, error } = await query(''));
  if (error) throw error;
  const rows = (journeys ?? []) as unknown as PreviewJourneyRow[];
  const { data: events, error: eventsError } = rows.length
    ? await supabase
        .from('shipment_tracking_events')
        .select('tracking_journey_id, event_type, location_name, occurred_at')
        .in(
          'tracking_journey_id',
          rows.map((row) => row.id)
        )
        .eq('is_estimated', false)
        .order('occurred_at', { ascending: false })
    : { data: [], error: null };
  if (eventsError) throw eventsError;
  return rows.map((row, index) => {
    const latest =
      (events ?? []).find((e) => e.tracking_journey_id === row.id) ?? null;
    return {
      shipment_id: row.shipment_tracking_links?.[0]?.shipment_id ?? -index - 1,
      identifier: row.identifier_value,
      mode: row.mode,
      state: (row.status === 'scheduled'
        ? 'scheduled'
        : row.status === 'rejected' ||
            row.status === 'failed' ||
            row.provider_status === 'UNTRACKED'
          ? 'not_found'
          : latest || row.provider_status !== 'NEW'
            ? 'tracking'
            : 'locating') as TrackingActivationState,
      journey_id: row.id,
      registration_opens_on: row.registration_opens_on,
      live_eta: row.live_eta,
      destination_name: row.destination_name,
      latest_event: latest,
      has_route: Boolean(row.route_synced_at),
    } satisfies TrackingActivationShipment;
  });
}
